/**
 * Recovery deep-link reader for the checkout island.
 *
 * The abandoned-cart mail links to `/lp/<slug>?rc=<signed token>`. The LP route
 * handler never reads `rc` — the served HTML stays byte-identical (so the
 * lp-golden gate never moves) and nothing per-visitor is baked into the
 * CDN-cached markup. The reader lives HERE, inside the island bundle, and runs
 * in the browser after the page is served.
 *
 * The bundle also ships on `scroll_direct` landing pages purely for the
 * installments messaging, and those pages have no `#mdb-ci-root`. That is the
 * structural guard: no root, no network call, no prefill — a misclassified LP
 * degrades to a plain visit, never to a broken page.
 */

/** Allowlisted resolve response (Go: recoverSessionResponse). */
export interface RecoverySessionResponse {
  status: "ok" | "invalid" | "recovered";
  email?: string;
  first_name?: string;
  last_name?: string;
  country?: string;
  product_id?: string;
  lp_slug?: string;
  lp_id?: string;
}

/** The four fields the island can prefill. Charge inputs are NOT among them. */
export interface RecoveryPrefill {
  email?: string;
  firstName?: string;
  lastName?: string;
  billingCountry?: string;
}

/** Plan list the island actually sells, used to bound the plan preselect. */
export interface RecoveryPlanConfig {
  productId?: string;
  tierProductIds?: string[];
  apiUrl?: string;
}

export const RECOVERY_PARAM = "rc";
export const ISLAND_ROOT_ID = "mdb-ci-root";

/** Reads the token out of a query string. Empty/absent → null. */
export function parseRecoveryToken(search: string): string | null {
  try {
    const token = new URLSearchParams(search).get(RECOVERY_PARAM);
    return token && token.trim() ? token.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Removes ONLY `rc` from a URL, keeping every other param and the hash.
 *
 * The token must not survive into a reload (the resolve POST would count a
 * second click), into a replay/analytics URL capture beyond the first frame, or
 * into the `document.referrer` the payment providers see.
 */
export function stripRecoveryParam(href: string): string {
  try {
    const u = new URL(href);
    if (!u.searchParams.has(RECOVERY_PARAM)) return href;
    u.searchParams.delete(RECOVERY_PARAM);
    const query = u.searchParams.toString();
    return `${u.origin}${u.pathname}${query ? `?${query}` : ""}${u.hash}`;
  } catch {
    return href;
  }
}

/**
 * A plan the island does not sell can never be preselected: the resolve reports
 * whatever the cart's landing page configured, and a token must not be able to
 * put an arbitrary product into the checkout.
 */
export function pickPlan(
  productId: string | undefined,
  config: RecoveryPlanConfig
): string | undefined {
  const pid = (productId || "").trim();
  if (!pid) return undefined;
  const allowed = new Set<string>();
  if (config.productId) allowed.add(config.productId);
  for (const id of config.tierProductIds || []) allowed.add(id);
  return allowed.has(pid) ? pid : undefined;
}

/**
 * Precedence, per the spec's D5:
 *
 *  - Email: the cart's address is the ground truth for THIS recovery and
 *    OVERRIDES a stored one. On a shared device the stored prefill may hold
 *    someone else's address, and the typed checkout email is the contact
 *    identity.
 *  - Names and country: fill gaps only — what the buyer typed last time is
 *    richer than the contact record.
 *  - An empty incoming value never clears a current one.
 */
export function mergePrefill(
  current: RecoveryPrefill,
  incoming: RecoveryPrefill
): RecoveryPrefill {
  const take = (inc?: string, cur?: string) => (inc && inc.trim() ? inc.trim() : cur || "");
  const fill = (cur?: string, inc?: string) =>
    cur && cur.trim() ? cur : inc && inc.trim() ? inc.trim() : "";
  return {
    email: take(incoming.email, current.email),
    firstName: fill(current.firstName, incoming.firstName),
    lastName: fill(current.lastName, incoming.lastName),
    billingCountry: fill(current.billingCountry, incoming.billingCountry),
  };
}

/** Buffered prefill + the live applier, mirroring the selectPlan pattern. */
declare global {
  interface Window {
    __mdbCiPendingPrefill?: RecoveryPrefill;
  }
}

/**
 * The narrow slice of `window` this module touches.
 *
 * Declared as an interface (rather than reaching for the global) so the runner
 * is testable in the repo's node-environment vitest, which has no DOM. The
 * production call passes the real window.
 */
export interface RecoveryWindow {
  MDBCheckout?: {
    applyPrefill?: (p: RecoveryPrefill) => void;
    selectPlan?: (pid: string, opts?: { installments?: boolean }) => void;
  };
  __mdbCiPendingPrefill?: RecoveryPrefill;
  __mdbCiPendingPlan?: string | { pid: string; installments?: boolean };
  __mdbCiMount?: () => void;
  history?: { replaceState: (data: unknown, unused: string, url: string) => void };
}

function applyPrefill(win: RecoveryWindow, prefill: RecoveryPrefill): void {
  const live = win.MDBCheckout?.applyPrefill;
  if (live) {
    live(prefill);
    return;
  }
  // Not mounted yet: buffer it, and the mount effect drains it. Together with
  // the live branch this is race-free in both directions.
  win.__mdbCiPendingPrefill = prefill;
}

function applyPlan(win: RecoveryWindow, pid: string): void {
  const live = win.MDBCheckout?.selectPlan;
  if (live) {
    live(pid);
    return;
  }
  win.__mdbCiPendingPlan = pid;
}

/**
 * Resolves the token and hands the island its prefill + plan, then jumps to the
 * checkout section.
 *
 * The jump is INSTANT (`behavior: "auto"`) and `block: "start"`, i.e. exactly
 * what a native `#anchor` link does: the checkout heading sits at the top of
 * the viewport and the whole sales page stays reachable by scrolling up. A
 * smooth scroll over ten screens would take seconds and drag the scroll-depth
 * tracker through every section on the way.
 *
 * Every failure — bad token, recovered cart, dead endpoint, CORS, timeout —
 * ends as a plain landing-page visit. The LP must never depend on the resolver.
 */
export async function runRecoveryDeepLink(
  config: RecoveryPlanConfig,
  deps?: {
    fetchImpl?: typeof fetch;
    doc?: Pick<Document, "getElementById">;
    loc?: { search: string; href: string };
    win?: RecoveryWindow;
  }
): Promise<"skipped" | "no-root" | "invalid" | "applied"> {
  const doc = deps?.doc ?? (typeof document !== "undefined" ? document : undefined);
  const loc = deps?.loc ?? (typeof location !== "undefined" ? location : undefined);
  const win = deps?.win ?? (typeof window !== "undefined" ? window : undefined);
  if (!doc || !loc || !win) return "skipped";

  const token = parseRecoveryToken(loc.search);
  if (!token) return "skipped";

  const root = doc.getElementById(ISLAND_ROOT_ID);
  if (!root) return "no-root";

  const fetchImpl = deps?.fetchImpl ?? fetch;
  const apiBase = config.apiUrl || "https://crm-app.makedesignerbags.com";

  let body: RecoverySessionResponse | null = null;
  try {
    const res = await fetchImpl(`${apiBase}/api/v1/checkout/recover-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    // A 429 from the resolver's rate limiter is treated exactly like an invalid
    // token: no prefill, and `rc` is still stripped below (no reload retry).
    if (res.ok) body = (await res.json()) as RecoverySessionResponse;
  } catch {
    /* the LP never depends on the resolver */
  }

  // Strip regardless of the outcome: a failed resolve must not leave a token in
  // the address bar for the next reload to retry.
  try {
    const cleaned = stripRecoveryParam(loc.href);
    if (cleaned !== loc.href) win.history?.replaceState(null, "", cleaned);
  } catch {
    /* best effort */
  }

  if (!body || body.status !== "ok") return "invalid";

  applyPrefill(win, {
    email: body.email,
    firstName: body.first_name,
    lastName: body.last_name,
    billingCountry: body.country,
  });

  const pid = pickPlan(body.product_id, config);
  if (pid) applyPlan(win, pid);

  try {
    win.__mdbCiMount?.();
  } catch {
    /* mount is best-effort; the lazy observer still covers it */
  }
  try {
    root.scrollIntoView({ behavior: "auto", block: "start" });
  } catch {
    /* older browsers ignore the options object */
  }
  return "applied";
}
